import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FileTextOutlined, MessageOutlined } from '@ant-design/icons';
import AutoDemo, { appear, typed, typingDone } from './AutoDemo';
import { AppFrame, AssistantPanel, EndCard } from './demoKit';
import { buildMarketDataScenes } from './MarketDataDemo';
import { heroDemoContent } from './heroDemoContent';
import './HeroDemo.css';

// Tendera in 45 seconds for the Home hero: ask market data about upcoming projects, search notices, open one with
// its attachments and let the AI summarise it. Replaces julkisethankinnat4.mp4.

const SearchPage = ({ c, t, keyword, pressed, rowsAt, expandAt }) => (
    <>
        <div className="hd-title">{c.search.title}</div>
        <div className="hd-form">
            <span className="hd-form__label">{c.search.label}</span>
            <span className="hd-form__input">
                {keyword && <span className="hd-chip">{keyword}</span>}
                {!keyword && <i className="dk-caret" />}
            </span>
            <span className={`hd-form__button${pressed ? ' is-pressed' : ''}`}>{c.search.button}</span>
        </div>
        <div className="hd-results">
            <div className="hd-results__head" style={appear(t, rowsAt - 200)}>{c.search.columns.map((col) => <span key={col}>{col}</span>)}</div>
            {c.search.rows.map((row, i) => (
                <React.Fragment key={row[2]}>
                    <div className={`hd-results__row${i === 0 && t > expandAt - 500 ? ' is-hover' : ''}`} style={appear(t, rowsAt + i * 300)}>
                        <span className={`hd-scope is-${row[0].toLowerCase()}`}>{row[0]}</span>
                        <span>{row[1]}</span>
                        <span className="hd-results__title">{row[2]}</span>
                        <span>{row[3]}</span>
                        <span>{row[4]}</span>
                        <span>{row[5]}</span>
                    </div>
                    {i === 0 && (
                        <div className="hd-expand" style={{ maxHeight: t > expandAt ? 120 : 0, opacity: t > expandAt ? 1 : 0 }}>
                            <p>{c.search.description}</p>
                            <span className="hd-link">{c.search.open}</span>
                        </div>
                    )}
                </React.Fragment>
            ))}
        </div>
    </>
);

export const NoticePage = ({ c, t, rowsAt = 0, askHover = false }) => (
    <>
        <div className="hd-notice">
            <div className="hd-notice__top">
                <span className={`hd-scope hd-scope--big is-${c.notice.scope.toLowerCase()}`}>{c.notice.scope}</span>
                <span className="hd-notice__title">{c.notice.title}</span>
            </div>
            <div className="hd-notice__meta">
                {c.notice.meta.map((m) => <span key={m}>{m}</span>)}
                <span className="is-deadline">{c.notice.deadline}</span>
            </div>
            <div className="hd-notice__actions">
                <span className={`hd-ask${askHover ? ' is-hover' : ''}`}><MessageOutlined /> {c.notice.ask}</span>
            </div>
            <div className="hd-tabs">
                {c.notice.tabs.map((tab, i) => <span key={tab} className={i === c.notice.tabs.length - 1 ? 'is-on' : ''}>{tab}</span>)}
            </div>
        </div>
        <div className="hd-attachments">
            <div className="hd-attachments__title">{c.notice.attachmentsTitle}</div>
            {c.notice.attachments.map((name, i) => (
                <div key={name} className="hd-file" style={appear(t, rowsAt + i * 260)}>
                    <FileTextOutlined />
                    <span className="hd-file__name">{name}</span>
                    <span className="hd-file__btn">{c.notice.view}</span>
                    <span className="hd-file__btn is-primary">{c.notice.download}</span>
                </div>
            ))}
        </div>
    </>
);

/** The notice page with its attachments; the "Ask AI" button lights up at the end. */
export const buildNoticeScene = (c, title) => {
    const fileRows = 700;
    return {
        id: 'notice', title, duration: fileRows + c.notice.attachments.length * 260 + 2600,
        render: ({ t, duration }) => (
            <AppFrame active={c.nav.search}>
                <NoticePage c={c} t={t} rowsAt={fileRows} askHover={t > duration - 900} />
            </AppFrame>
        ),
    };
};

/** The notice AI chat slides in over the notice and summarises it: summary, key dates, selection. */
export const buildChatScene = (c, title, hold = 3400) => {
    const askedAt = 1500;
    const answerAt = askedAt + 700;
    const datesAt = answerAt + 1300;
    const criteriaAt = datesAt + c.chat.dates.length * 350 + 500;
    const answerDone = criteriaAt + c.chat.criteria.length * 450;
    return {
        id: 'chat', title, duration: answerDone + hold,
        render: ({ t }) => (
            <>
                <AppFrame active={c.nav.search}>
                    <NoticePage c={c} t={60000} />
                </AppFrame>
                <div className="dk-dim" style={{ opacity: Math.min(1, t / 300) }} />
                <AssistantPanel
                    className="hd-drawer"
                    style={{ transform: `translateX(${(1 - Math.min(1, t / 450)) * 100}%)` }}
                    title={c.chat.title}
                    placeholder={c.chat.placeholder}
                    input={t < askedAt ? typed(c.chat.question, t, 450, 30) : ''}
                    sending={t > askedAt - 250 && t < askedAt}
                    scroll={Math.max(0, Math.min(1, (t - criteriaAt) / 900))}
                >
                    <div className="hd-drawer__subtitle">{c.chat.subtitle}</div>
                    <div className="dk-bubble" style={appear(t, askedAt)}>{c.chat.question}</div>
                    <div className="dk-answer">
                        <div className="dk-answer__subtitle" style={appear(t, answerAt)}>{c.chat.summaryTitle}</div>
                        <p className="dk-answer__lead" style={appear(t, answerAt + 300)}>{c.chat.summary} <span className="dk-cite">{c.chat.source}</span></p>
                        <div className="dk-answer__subtitle" style={appear(t, datesAt)}>{c.chat.datesTitle}</div>
                        <div className="hd-dates">
                            {c.chat.dates.map(([label, value], i) => (
                                <div key={label} style={appear(t, datesAt + 250 + i * 350)}><span>{label}</span><b>{value}</b></div>
                            ))}
                        </div>
                        <div className="dk-answer__subtitle" style={appear(t, criteriaAt)}>{c.chat.criteriaTitle}</div>
                        <ul className="hd-bullets">
                            {c.chat.criteria.map((line, i) => <li key={line} style={appear(t, criteriaAt + 250 + i * 450)}>{line}</li>)}
                        </ul>
                    </div>
                </AssistantPanel>
            </>
        ),
    };
};

export const buildHeroScenes = (c) => {
    const [ask, projects] = buildMarketDataScenes(c.market);
    const keywordTyped = 500;
    const keywordDone = typingDone(c.search.keyword, keywordTyped, 90);
    const searchRows = keywordDone + 900;
    const expandAt = searchRows + c.search.rows.length * 300 + 1100;
    return [
        { ...ask, id: 'market', title: c.chapters[0] },
        { ...projects, id: 'projects', title: c.chapters[1] },
        {
            id: 'search', title: c.chapters[2], duration: expandAt + 3200,
            render: ({ t }) => (
                <AppFrame active={c.nav.search}>
                    <SearchPage c={c} t={t} keyword={typed(c.search.keyword, t, keywordTyped, 90)}
                        pressed={t > keywordDone + 300 && t < keywordDone + 700} rowsAt={searchRows} expandAt={expandAt} />
                </AppFrame>
            ),
        },
        buildNoticeScene(c, c.chapters[3]),
        buildChatScene(c, c.chapters[4]),
        { id: 'end', title: c.chapters[5], duration: 5200, render: ({ t }) => <EndCard t={t} lines={c.endLines} /> },
    ];
};

// `poster` shows one still frame (the AI summary of a notice) for the teaser that opens the full tour.
const HeroDemo = ({ onExpand, poster = false, className = '' }) => {
    const { t, i18n } = useTranslation();
    const content = heroDemoContent(i18n.language);
    const scenes = useMemo(() => buildHeroScenes(content), [content]);
    const posterAt = useMemo(() => {
        const chat = scenes.findIndex((s) => s.id === 'chat');
        return scenes.slice(0, chat).reduce((sum, s) => sum + s.duration, 0) + scenes[chat].duration - 1500;
    }, [scenes]);
    return (
        <AutoDemo
            scenes={scenes}
            autoPlay={!poster}
            controls={!poster}
            startAt={poster ? posterAt : 0}
            onExpand={onExpand}
            className={`hero-demo ${className}`}
            label={t('pages.home.watchIntroVideo', 'Katso Tenderan esittelyvideo')}
            labels={{ play: t('demo.play', 'Toista'), pause: t('demo.pause', 'Tauko'), expand: t('pages.home.expandVideo', 'Expand video') }}
        />
    );
};

export default HeroDemo;
